/**
 * Collapsible CLI-command preview with a Copy-to-clipboard button.
 *
 * Shared by every maintenance / provisioning form so operators can
 * review *and copy* the exact command block a job will send to the OLT
 * before submitting the (destructive) job. The rendered block comes
 * from the backend maintenance preview endpoints (or a client-side
 * render for the bulk Pending flow).
 *
 * Copy reuses the same robust path as ``CopyableCell``:
 * ``navigator.clipboard.writeText`` with a hidden-textarea +
 * ``execCommand`` fallback for insecure contexts.
 */
import { Check, ClipboardCopy } from "lucide-react";
import { useCallback, useState } from "react";

import { cn } from "../lib/cn";
import { useToast } from "./Toast";

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Insecure-context fallback. Hidden textarea + execCommand.
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "absolute";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

export function CommandPreview({
  text,
  label = "Commands to run",
  emptyHint = "Nothing to preview yet.",
  className,
}: {
  /** The full CLI block (newline-joined). Empty string → shows hint. */
  text: string;
  /** Heading shown above the block. */
  label?: string;
  /** Shown when ``text`` is empty. */
  emptyHint?: string;
  className?: string;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    if (!text) return;
    try {
      await writeClipboard(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.push("Commands copied to clipboard", "success");
    } catch {
      toast.push("Copy failed — check browser permissions", "error");
    }
  }, [text, toast]);

  return (
    <div
      className={cn("rounded bg-slate-950/60 shadow-border", className)}
    >
      <div className="flex items-center justify-between px-3 py-1.5">
        <span className="text-[11px] uppercase tracking-wide text-slate-400">
          {label}
        </span>
        <button
          type="button"
          onClick={onCopy}
          disabled={!text}
          className={[
            "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px]",
            "border-slate-700 text-slate-200 hover:bg-slate-800",
            "transition-[scale,background-color,color] duration-150 ease-out",
            "active:enabled:scale-[0.96]",
            "disabled:opacity-40 disabled:hover:bg-transparent",
          ].join(" ")}
          title="Copy the command block to the clipboard"
        >
          {/* Copy→check cross-fade: both icons stay mounted (one absolute),
              so the swap animates in AND out. ClipboardCopy is the
              layout-defining icon; Check overlays it. */}
          <span className="relative inline-flex h-3 w-3 items-center justify-center">
            <Check
              className={cn(
                "absolute h-3 w-3 text-emerald-400",
                "transition-[opacity,filter,scale] duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
                copied
                  ? "scale-100 opacity-100 blur-0"
                  : "scale-[0.25] opacity-0 blur-[4px]",
              )}
            />
            <ClipboardCopy
              className={cn(
                "h-3 w-3",
                "transition-[opacity,filter,scale] duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
                copied
                  ? "scale-[0.25] opacity-0 blur-[4px]"
                  : "scale-100 opacity-100 blur-0",
              )}
            />
          </span>
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      {text ? (
        <pre className="max-h-96 overflow-auto rounded-b bg-slate-950 p-2 font-mono text-[10px] leading-relaxed text-slate-300">
          {text}
        </pre>
      ) : (
        <p className="px-3 pb-2 text-[11px] text-slate-500">{emptyHint}</p>
      )}
    </div>
  );
}
